import { useEffect, useRef, useState } from 'react';

const SERVICES = [
  {
    icon: 'ri-paint-brush-line',
    title: '페인트 시공',
    desc: '주거 및 상업 공간의 내·외벽 페인트 시공을 전문으로 합니다. 친환경 도료 사용과 꼼꼼한 전처리 작업으로 오래가는 아름다운 마감을 제공합니다.',
    tags: ['내벽 도장', '외벽 도장', '친환경 도료', '아파트/빌딩'],
    image: 'https://storage.readdy-site.link/project_files/4acba870-6354-40bb-a23a-0b6bf3db091a/fe85b715-2d89-4683-9740-55f1f6774e44_KakaoTalk_20260326_121305616_03.jpg?v=d4f0e495c608ad92bf621b51831b2358',
  },
  {
    icon: 'ri-drop-line',
    title: '도장 서비스',
    desc: '금속, 목재, 콘크리트 등 다양한 소재에 맞는 특수 도장 처리를 시행합니다. 내구성과 미적 완성도를 모두 충족하는 고품질 마감 서비스입니다.',
    tags: ['특수 도장', '금속 도장', '목재 도장', '방청 도료'],
    image: 'https://storage.readdy-site.link/project_files/4acba870-6354-40bb-a23a-0b6bf3db091a/6771d920-c90f-4ecb-8b18-1df132cd2dda_KakaoTalk_20260326_123647390.jpg?v=b5f311e4d40526c2477125d212e27f64',
  },
  {
    icon: 'ri-shield-check-line',
    title: '방수 공사',
    desc: '욕실, 베란다, 옥상, 지하주차장 등 습기 취약 부위의 방수 공사를 전문으로 합니다. 우레탄·시트방수·탄성도막 등 최적의 공법으로 완벽하게 마감합니다.',
    tags: ['옥상 방수', '욕실 방수', '베란다 방수', '지하주차장'],
    image: 'https://storage.readdy-site.link/project_files/4acba870-6354-40bb-a23a-0b6bf3db091a/7eedcb3e-667b-42a0-9b7f-1adb4f1604d5_KakaoTalk_20260326_123647390_02.jpg?v=807f5597b2728742127e59a7ab97a899',
  },
];

export default function ServicesSection() {
  const [visible, setVisible] = useState(false);
  const ref = useRef<HTMLElement>(null);

  useEffect(() => {
    const observer = new IntersectionObserver(
      ([entry]) => { if (entry.isIntersecting) setVisible(true); },
      { threshold: 0.15 }
    );
    if (ref.current) observer.observe(ref.current);
    return () => observer.disconnect();
  }, []);

  const scrollToContact = () => {
    const el = document.querySelector('#contact');
    if (el) el.scrollIntoView({ behavior: 'smooth' });
  };

  return (
    <section id="services" ref={ref} className="py-20 md:py-28 bg-white">
      <div className="max-w-7xl mx-auto px-4 md:px-8">

        {/* Title */}
        <div
          className={`text-center mb-14 transition-all duration-700 ${
            visible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-6'
          }`}
        >
          <span className="inline-block mb-3 px-4 py-1 border border-stone-300 text-stone-500 text-sm font-semibold tracking-widest uppercase rounded-full">
            Services
          </span>
          <h2 className="text-4xl md:text-5xl font-bold text-stone-900 mt-3 mb-4">전문 시공 서비스</h2>
          <p className="text-stone-500 text-lg md:text-xl max-w-xl mx-auto">
            고객의 공간을 완벽하게 변화시키는<br />
            라인 인테리어의 핵심 서비스입니다
          </p>
        </div>

        {/* Service Cards */}
        <div className="grid grid-cols-1 md:grid-cols-3 gap-5 md:gap-6">
          {SERVICES.map((svc, idx) => (
            <div
              key={svc.title}
              className={`rounded-2xl overflow-hidden group transition-all duration-700 hover:-translate-y-2 cursor-default ${
                visible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-8'
              }`}
              style={{ transitionDelay: `${idx * 120}ms` }}
            >
              {/* Image — 카드 상단을 꽉 채움 */}
              <div className="w-full h-56 md:h-64 overflow-hidden">
                <img
                  src={svc.image}
                  alt={svc.title}
                  className="w-full h-full object-cover object-center transition-transform duration-500 group-hover:scale-105"
                />
              </div>

              {/* Text — 이미지 바로 아래 어두운 배경으로 하나의 덩어리처럼 */}
              <div className="bg-stone-900 p-6 md:p-7">
                <div className="flex items-center gap-3 mb-4">
                  <div className="w-9 h-9 flex items-center justify-center rounded-full bg-blue-500/20 flex-shrink-0">
                    <i className={`${svc.icon} text-blue-400 text-lg`} />
                  </div>
                  <h3 className="text-xl font-bold text-white">{svc.title}</h3>
                </div>
                <p className="text-stone-400 text-sm leading-relaxed mb-5">{svc.desc}</p>
                <div className="flex flex-wrap gap-2">
                  {svc.tags.map((tag) => (
                    <span
                      key={tag}
                      className="px-2.5 py-1 bg-white/10 text-white/60 text-xs rounded-md font-medium"
                    >
                      {tag}
                    </span>
                  ))}
                </div>
              </div>
            </div>
          ))}
        </div>

        {/* Bottom CTA */}
        <div
          className={`mt-14 text-center transition-all duration-700 delay-500 ${
            visible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-4'
          }`}
        >
          <div className="inline-flex flex-col sm:flex-row items-center gap-4 bg-blue-600 text-white rounded-xl px-8 py-6">
            <div className="text-center sm:text-left">
              <p className="font-bold text-lg">지금 바로 무료로<br />견적을 받아보세요.</p>
              <p className="text-white/80 text-base mt-0.5">24시간 이내 전문 상담사가<br />연락드립니다.</p>
            </div>
            <button
              onClick={scrollToContact}
              className="flex items-center gap-2 bg-white text-blue-600 font-bold px-6 py-2.5 rounded-lg text-base hover:bg-blue-50 transition-colors cursor-pointer whitespace-nowrap"
            >
              무료 견적 신청 <i className="ri-arrow-right-line" />
            </button>
          </div>
        </div>
      </div>
    </section>
  );
}
